import { faWhatsapp } from "@fortawesome/free-brands-svg-icons/faWhatsapp";
import { Tooltip } from "../../../../utils/local-antd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../../../components/Button";
import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { IntegrationSection } from "../IntegrationSection";
import { faQrcode } from "@fortawesome/free-solid-svg-icons/faQrcode";
import { useEffect, useState } from "react";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Spacing } from "../../../../theme/Spacing";
import { faMessage } from "@fortawesome/free-solid-svg-icons/faMessage";
import { Link, useNavigate } from "react-router-dom";
import { IntegrationWhatsAppDirectQRModal } from "../modals/IntegrationWhatsappDirectQRModal";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import { WarningModal } from "../../../../components/WarningModal";

const StyledButton = styled(Button)`
  background-color: transparent;
  padding: ${Spacing.xs};
  color: ${({ theme }) => theme.main};
  margin-inline-start: auto;
`;

const StyledLink = styled(Link)`
  background-color: transparent;
  padding: ${Spacing.xs};
  color: ${({ theme }) => theme.main};
  margin-inline-start: auto;

  &:hover {
    color: ${({ theme }) => theme.main};
  }
`;

export const IntegrationWhatsAppDirectSection: React.FC = () => {
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [disconnectModalVisible, setDisconnectModalVisible] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppDirectIntegration,
  );
  const canViewChats = integration?.config?.clientStatus === "authorized";

  const onGetQR = async () => {
    try {
      setLoading(true);
      const qrImage = await dispatch(
        integrationActions.getWhatsAppDirectQR({
          integrationId: integration!._id,
          userId,
        }),
      ).unwrap();

      setQrImage(qrImage.qrImage);
      setQrModalVisible(true);
    } catch (e) {
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  const onDisconnect = async () => {
    try {
      setDisconnectLoading(true);
      await dispatch(
        integrationActions.disconnectWhatsAppDirect({
          integrationId: integration!._id,
          userId,
        }),
      ).unwrap();
      await dispatch(
        integrationActions.getUserIntegrations({
          userId,
        }),
      ).unwrap();

      setDisconnectModalVisible(false);
      AtiraToast.success(
        t("integration.add-modal.whatsapp-direct.disconnect.modal.success"),
      );
    } catch (e) {
      AtiraToast.apiError(e);
    } finally {
      setDisconnectLoading(false);
    }
  };

  useEffect(() => {
    if (integration?._id && userId) {
      dispatch(
        integrationActions.syncWhatsappDirectStatus({
          integrationId: integration._id,
          userId,
        }),
      );
    }
  }, [dispatch, integration?._id, userId]);

  if (!integration) {
    return null;
  }

  return (
    <IntegrationSection
      enabled={canViewChats}
      icon={faWhatsapp}
      iconColor="whatsapp"
    >
      <Flex gap="s" alignItems="center" justifyContent="space-between" flex={1}>
        <Flex>
          <Text>{t("integration.add-modal.whatsapp-direct.button")}</Text>
        </Flex>

        <Flex gap="m" alignItems="center">
          {canViewChats ? (
            <Tooltip
              title={t(
                "integration.add-modal.whatsapp-direct.disconnect.tooltip",
              )}
            >
              <StyledButton
                icon={faTrash}
                onClick={() => setDisconnectModalVisible(true)}
                iconWidth="2xl"
              />
            </Tooltip>
          ) : null}

          {canViewChats ? (
            <Tooltip
              title={t(
                "integration.add-modal.whatsapp-direct.messages.tooltip",
              )}
            >
              <StyledLink to={`/integration/whatsapp-direct`}>
                <AtiraIcon icon={faMessage} size="2xl" />
              </StyledLink>
            </Tooltip>
          ) : null}

          <Tooltip
            title={t("integration.add-modal.whatsapp-direct.qr.tooltip")}
          >
            <StyledButton
              loading={loading}
              icon={faQrcode}
              onClick={onGetQR}
              iconWidth="2xl"
            />
          </Tooltip>
        </Flex>
      </Flex>

      <WarningModal
        isOpen={disconnectModalVisible}
        onClose={() => setDisconnectModalVisible(false)}
        onConfirm={onDisconnect}
        title={t(
          "integration.add-modal.whatsapp-direct.disconnect.modal.title",
        )}
        description={t(
          "integration.add-modal.whatsapp-direct.disconnect.modal.description",
        )}
        loading={disconnectLoading}
      />

      <IntegrationWhatsAppDirectQRModal
        open={qrModalVisible}
        onClose={() => setQrModalVisible(false)}
        qrImage={qrImage}
        integrationId={integration._id}
      />
    </IntegrationSection>
  );
};
